import { CommonModule } from '@angular/common';
import { Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { LucideAngularModule, ArrowLeft, Pencil, Plus, Save, ShoppingCart, Trash2 } from 'lucide-angular';
import { forkJoin, of } from 'rxjs';
import { DocumentConfig, Loa, LoaBillConfig, LoaEmd, LoaSchedule, Vendor, VendorDocument } from '../../../core/models/domain.models';
import { LoaService } from '../services/loa.service';
import { VendorsService } from '../../vendor/services/vendors.service';
import { ToastService } from '../../../shared/ui/toast.service';
import { VendorDocumentsService } from '../../vendor-document/services/vendor-documents.service';
import { AuthService } from '../../../core/services/auth.service';

const CATEGORIES = ['Buy', 'Sell', 'Banking'];
const LOA_TERMS = ['Short Term', 'Medium Term', 'Long Term'];
const RATE_TYPES = ['Fixed', 'MCP'];
const MCP_CONDITIONS = ['Average', 'Equal To', 'Greater Than', 'Less Than'];
const STATUS_OPTIONS = ['draft', 'pending_initial_approval', 'initial_approved', 'pending_accounts_approval', 'accounts_approved', 'pending_operation_approval', 'final_approved', 'rejected'];
const INITIAL_APPROVER_ROLE_KEYS = ['admin', 'super_admin', 'bd_rm', 'business_development_rm', 'trader', 'trader_staff', 'trading'];
const ACCOUNTS_APPROVER_ROLE_KEYS = ['accounts', 'account'];
const OPERATION_APPROVER_ROLE_KEYS = ['operations', 'operation', 'ops'];
const APPROVER_ROLE_KEYS = [...INITIAL_APPROVER_ROLE_KEYS, ...ACCOUNTS_APPROVER_ROLE_KEYS, ...OPERATION_APPROVER_ROLE_KEYS];
const BILL_TYPES = ['SOP', 'TMR', 'OA'];
const BILL_CYCLES = ['Days', 'Daily', 'Weekly', 'Monthly', 'Fortnightly', 'Custom'];
const OA_BILL_CYCLE_OPTIONS = [...BILL_CYCLES, 'Approval Wise'];
const SOP_RATE_TYPES = ['Fixed', 'MCP'];
const MCP_CONDITION_OPTIONS = ['Average', 'Equal', 'ADD', 'Less'];
const BANKING_PRIORITY_OPTIONS = ['Import', 'Export'];
const PROVISIONAL_STATUS_OPTIONS = ['Yes', 'No'];
const DEPOSIT_BY_OPTIONS = ['BG', 'DD', 'Cheque'];
const DELIVERY_POINTS = ['Regional Periphery', 'State Periphery', 'Client Periphery', 'Discom Periphery'];
const LOA_LEAD_OPTIONS = ['Lead', 'Tender'];
const DOC_STATUS_OPTIONS = ['Active', 'Inactive'];
const OA_CHARGE_ROWS = [
  'Transmission Charges',
  'Wheeling Charges',
  'SLDC Charges',
  'RLDC Charges',
  'Application Fee Charges',
  'Losses Charges',
];
type CycleRange = { id: string; from: string; to: string };
type OaChargeAllocation = {
  charge_type: string;
  payer: 'Buyer' | 'Seller' | 'Shared';
  buyer_percent: number;
  seller_percent: number;
};
type PenaltyQuantumSlab = {
  id: string;
  from_unit: number;
  to_unit: number;
  rate: number;
};
type LoaDocumentListRow = {
  key: string;
  documentType: string;
  originalName: string;
  fileSize: number | null;
  uploaded: boolean;
  id?: string;
};

type Mode = 'new' | 'edit' | 'view';

function buildTimeOptions(includeEndOfDay: boolean): string[] {
  const values: string[] = [];
  for (let hour = 0; hour <= 23; hour += 1) {
    for (let minute = 0; minute < 60; minute += 15) {
      values.push(`${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`);
    }
  }
  if (includeEndOfDay) values.push('24:00');
  return values;
}

@Component({
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, LucideAngularModule],
  templateUrl: './loa-form.page.html',
  styleUrl: './loa-form.page.scss',
})
export class LoaFormPage {
  protected readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly fb = inject(FormBuilder);
  private readonly loaService = inject(LoaService);
  private readonly documentsService = inject(VendorDocumentsService);
  private readonly vendorsService = inject(VendorsService);
  private readonly toast = inject(ToastService);
  protected readonly auth = inject(AuthService);

  protected readonly ArrowLeftIcon = ArrowLeft;
  protected readonly SaveIcon = Save;
  protected readonly PlusIcon = Plus;
  protected readonly PencilIcon = Pencil;
  protected readonly Trash2Icon = Trash2;
  protected readonly ShoppingCartIcon = ShoppingCart;

  protected readonly categoryOptions = CATEGORIES;
  protected readonly loaTermOptions = LOA_TERMS;
  protected readonly energyTypeOptions = signal<string[]>([]);
  protected readonly rateTypeOptions = RATE_TYPES;
  protected readonly mcpConditionOptions = MCP_CONDITIONS;
  protected readonly statusOptions = STATUS_OPTIONS;
  protected readonly billTypeOptions = BILL_TYPES;
  protected readonly billCycleOptions = BILL_CYCLES;
  protected readonly oaBillCycleOptions = OA_BILL_CYCLE_OPTIONS;
  protected readonly sopRateTypeOptions = SOP_RATE_TYPES;
  
  protected readonly billingMcpConditionOptions = MCP_CONDITION_OPTIONS;
  protected readonly bankingPriorityOptions = BANKING_PRIORITY_OPTIONS;
  protected readonly provisionalStatusOptions = PROVISIONAL_STATUS_OPTIONS;
  protected readonly depositByOptions = DEPOSIT_BY_OPTIONS;
  protected readonly deliveryPointOptions = DELIVERY_POINTS;
  protected readonly loaLeadOptions = LOA_LEAD_OPTIONS;
  protected readonly docStatusOptions = DOC_STATUS_OPTIONS;
  protected readonly fromTimeOptions = buildTimeOptions(false);
  protected readonly toTimeOptions = buildTimeOptions(true);

  protected readonly mode = signal<Mode>('new');
  protected readonly loaId = signal<string | null>(null);
  protected readonly viewSource = signal('loa');
  protected readonly currentLoa = signal<Loa | null>(null);
  protected readonly loading = signal(false);
  protected readonly vendors = signal<Vendor[]>([]);
  protected readonly schedules = signal<LoaSchedule[]>([]);
  protected readonly emds = signal<LoaEmd[]>([]);
  protected readonly billConfigs = signal<LoaBillConfig[]>([]);
  protected readonly draftSchedules = signal<LoaSchedule[]>([]);
  protected readonly draftEmds = signal<LoaEmd[]>([]);
  protected readonly draftBillConfigs = signal<LoaBillConfig[]>([]);
  protected readonly documentConfigs = signal<DocumentConfig[]>([]);
  protected readonly draftDocumentConfigs = signal<DocumentConfig[]>([]);
  protected readonly loaDocuments = signal<VendorDocument[]>([]);

  protected readonly showScheduleForm = signal(false);
  protected readonly editingScheduleId = signal<string | null>(null);
  protected readonly activeScheduleDirection = signal<'import' | 'export'>('import');
  protected readonly showEmdForm = signal(false);
  protected readonly editingEmdId = signal<string | null>(null);
  protected readonly showBillForm = signal(false);
  protected readonly editingBillConfigId = signal<string | null>(null);
  protected readonly showDocumentForm = signal(false);
  protected readonly editingDocumentConfigId = signal<string | null>(null);
  protected readonly showDocumentUploadForm = signal(false);
  protected readonly uploadingLoaDocument = signal(false);
  protected readonly selectedUploadDocType = signal('');
  protected readonly selectedUploadOtherDocType = signal('');
  protected readonly selectedUploadFiles = signal<File[]>([]);
  protected readonly billingRuns = signal({ SOP: true, TMR: false, OA: true });
  protected readonly billCycleSelection = signal('');
  protected readonly showBillCycleViewModal = signal(false);
  protected readonly showOaChargesViewModal = signal(false);
  protected readonly customCycleRanges = signal<CycleRange[]>([]);
  protected readonly rangeEditor = this.fb.nonNullable.group({
    from: [''],
    to: [''],
  });
  protected readonly editingCycleRangeId = signal<string | null>(null);
  protected readonly oaChargeAllocations = signal<OaChargeAllocation[]>([]);
  protected readonly oaChargeViewRows = signal<OaChargeAllocation[]>([]);
  protected readonly penaltySlabs = signal<PenaltyQuantumSlab[]>([]);
  protected readonly editingPenaltySlabId = signal<string | null>(null);
  private tempIdCounter = 0;
  protected readonly penaltyEnabled = signal(false);
  protected readonly penaltyModeSelection = signal<'fixed_rate' | 'quantum_wise'>('fixed_rate');
  protected readonly penaltySlabForm = this.fb.nonNullable.group({
    from_unit: [''],
    to_unit: [''],
    rate: [''],
  });

  protected readonly form = this.fb.nonNullable.group({
    loa_number: ['', Validators.required],
    vendor_id: ['', Validators.required],
    category: ['Buy', Validators.required],
    loa_term: [''],
    issue_date: ['', Validators.required],
    effective_from: [''],
    effective_to: [''],
    loa_lead: [''],
    is_provisional: [false],
    energy_type: [''],
    delivery_point: [''],
    state: [''],
    rate_type: [''],
    mcp_condition: [''],
    mcp_value: [''],
    fixed_rate: [''],
    penalty_applicable: [false],
    penalty_mode: ['fixed_rate'],
    penalty_fixed_rate: [''],
    banking_priority: [''],
    revision_limit: [''],
    remarks: [''],
    status: ['draft', Validators.required],
  });

  protected readonly scheduleForm = this.fb.nonNullable.group({
    from_date: ['', Validators.required],
    to_date: ['', Validators.required],
    from_time: ['', Validators.required],
    to_time: ['', Validators.required],
    quantum_mw: [''],
    return_percentage: [''],
    arrangement: ['1', Validators.required],
    direction: ['import', Validators.required],
  });

  protected readonly emdForm = this.fb.nonNullable.group({
    security_deposit_type: ['', Validators.required],
    issue_date: ['', Validators.required],
    effective_from: ['', Validators.required],
    effective_to: ['', Validators.required],
    bank_name: ['', Validators.required],
    reference_number: ['', Validators.required],
    amount: ['', Validators.required],
  });

  protected readonly billConfigForm = this.fb.nonNullable.group({
    bill_type: ['SOP', Validators.required],
    bill_cycle: ['', Validators.required],
    effective_date: ['', Validators.required],
    rate_type: ['MCP', Validators.required],
    rate_type_unit: ['Rs/kWh', Validators.required],
    mcp_condition: [''],
    mcp_rate: [''],
    tmr_rate: [''],
    rate: ['', Validators.required],
    due_days: ['', Validators.required],
    rebate: ['', Validators.required],
    rebate_days: ['', Validators.required],
    discount_price: [''],
    custom_bill_cycle_view: [''],
  });

  protected readonly documentForm = this.fb.nonNullable.group({
    module_name: ['LOA Module', Validators.required],
    client_types: ['Buy, Sell, Banking', Validators.required],
    doc_title: ['', Validators.required],
    allowed_file_types: ['PDF', Validators.required],
    mandatory: [false],
    max_file_size_mb: [10, [Validators.required, Validators.min(1)]],
    status: ['Active', Validators.required],
    effective_date: [''],
  });

  protected readonly readOnly = computed(() => this.mode() === 'view');
  protected readonly selectedCategory = computed(() =>
    this.normalizeLoaCategory(this.currentLoa()?.category || this.form.controls.category.value),
  );
  protected readonly isBanking = computed(() => this.selectedCategory() === 'Banking');
  protected readonly isMcp = computed(() => this.form.controls.rate_type.value === 'MCP');
  protected readonly isFixedRate = computed(() => this.form.controls.rate_type.value === 'Fixed');
  protected readonly selectedCategoryUpper = computed(() => this.selectedCategory().toUpperCase());
  protected readonly pageHeadingTitle = computed(() => {
    if (this.mode() === 'edit') return `Edit ${this.selectedCategoryUpper()} LOA`;
    if (this.mode() === 'view') return `View ${this.selectedCategoryUpper()} LOA`;
    return `Create ${this.selectedCategoryUpper()} LOA`;
  });
  protected readonly pageHeadingKicker = computed(() => {
    if (this.mode() === 'edit') return `${this.selectedCategoryUpper()} LOA`;
    if (this.mode() === 'view') return `${this.selectedCategoryUpper()} LOA`;
    return 'LOA Management';
  });
  protected readonly loaStatus = computed(
    () => (this.currentLoa()?.loa_status || this.form.controls.status.value || 'draft').toLowerCase(),
  );
  protected readonly selectedVendor = computed(() =>
    this.vendors().find((item) => item.id === this.form.controls.vendor_id.value) || null,
  );
  protected readonly isGeneratorEntity = computed(() => {
    const vendor = this.selectedVendor();
    const vendorType = (vendor?.vendor_type || '').trim().toLowerCase();
    return vendorType === 'generator';
  });
  protected readonly isEnergyTypeRequired = computed(() => this.isGeneratorEntity());
  protected readonly userRoleKey = computed(() =>
    (this.auth.user()?.role || '').toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_'),
  );
  protected readonly isVendorUser = computed(() => this.auth.user()?.role === 'vendor');
  protected readonly isApprover = computed(() => {
    const role = this.userRoleKey();
    return APPROVER_ROLE_KEYS.includes(role);
  });
  protected readonly canInitialApprove = computed(() => {
    const server = this.currentLoa()?.can_initial_approve;
    if (server !== null && server !== undefined) return !!server;
    const role = this.userRoleKey();
    return INITIAL_APPROVER_ROLE_KEYS.includes(role) && this.loaStatus() === 'pending_initial_approval';
  });
  protected readonly canAccountsApprove = computed(() => {
    const server = this.currentLoa()?.can_accounts_approve;
    if (server !== null && server !== undefined) return !!server;
    const role = this.userRoleKey();
    return ACCOUNTS_APPROVER_ROLE_KEYS.includes(role) && this.loaStatus() === 'pending_accounts_approval';
  });
  protected readonly canFinalApprove = computed(() => {
    const server = this.currentLoa()?.can_final_approve;
    if (server !== null && server !== undefined) return !!server;
    const role = this.userRoleKey();
    return OPERATION_APPROVER_ROLE_KEYS.includes(role) && this.loaStatus() === 'pending_operation_approval';
  });
  protected readonly hideFinalApprovalForExternalView = computed(() => this.mode() === 'view' && this.viewSource() !== 'loa');
  protected readonly canVisibleFinalApprove = computed(() => this.canFinalApprove() && !this.hideFinalApprovalForExternalView());
  protected readonly displayLoaStatus = computed(() => {
    const status = this.loaStatus();
    if (this.hideFinalApprovalForExternalView() && status === 'final_approved') return 'approved';
    return status;
  });
  protected readonly hideFinancialSectionsForOperation = computed(
    () => ['operations', 'operation', 'ops'].includes(this.userRoleKey()),
  );
  protected readonly canShowApprovalActions = computed(() => {
    if (!this.loaId() || this.mode() !== 'view') return false;
    return this.canInitialApprove() || this.canAccountsApprove() || this.canVisibleFinalApprove();
  });
  protected readonly canEditCurrentLoa = computed(() => {
    if (!this.loaId()) return !this.readOnly();
    const server = this.currentLoa()?.can_edit;
    if (server !== null && server !== undefined) return !!server;
    return this.canUserEditLoa(this.currentLoa());
  });
  protected readonly canManageLoaUploads = computed(() => {
    if (this.mode() === 'view') return false;
    if (!this.loaId()) return true;
    if (!this.canEditCurrentLoa()) return false;
    return ['draft', 'pending_initial_approval', 'pending_accounts_approval', 'pending_operation_approval', 'final_approved', 'approved'].includes(this.loaStatus());
  });
  protected readonly isApprovedLoa = computed(() => ['final_approved', 'approved'].includes(this.loaStatus()));
  protected readonly provisionalValue = computed(() => (this.form.controls.is_provisional.value ? 'Yes' : 'No'));
  protected readonly displaySchedules = computed(() => (this.loaId() ? this.schedules() : this.draftSchedules()));
  protected readonly displayEmds = computed(() => (this.loaId() ? this.emds() : this.draftEmds()));
  protected readonly displayBillConfigs = computed(() => (this.loaId() ? this.billConfigs() : this.draftBillConfigs()));
  protected readonly displayDocumentConfigs = computed(() =>
    this.documentConfigs().length ? this.documentConfigs() : this.draftDocumentConfigs(),
  );
  protected readonly activeLoaDocumentConfigs = computed(() =>
    this.displayDocumentConfigs().filter(
      (item) => this.isDocumentConfigForSelectedCategory(item) && this.isDocumentConfigForSelectedEntityType(item),
    ),
  );
  protected readonly loaDocumentRows = computed<LoaDocumentListRow[]>(() => {
    const uploadedRows: LoaDocumentListRow[] = this.loaDocuments().map((doc) => ({
      key: `uploaded-${doc.id}`,
      documentType: doc.document_type || '-',
      originalName: doc.original_name || '-',
      fileSize: Number.isFinite(doc.file_size) ? doc.file_size : null,
      uploaded: true,
      id: doc.id,
    }));
    const uploadedTypes = new Set(
      uploadedRows.map((row) => this.normalizeDocToken(row.documentType)).filter((value) => !!value),
    );
    const pendingRows: LoaDocumentListRow[] = this.activeLoaDocumentConfigs()
      .map((item) => (item.doc_title || item.document_name || '').trim())
      .filter((name) => !!name)
      .filter((name) => !uploadedTypes.has(this.normalizeDocToken(name)))
      .map((name) => ({
        key: `pending-${this.normalizeDocToken(name)}`,
        documentType: name,
        originalName: '-',
        fileSize: null,
        uploaded: false,
      }));
    return [...uploadedRows, ...pendingRows];
  });
  protected readonly missingMandatoryLoaDocumentNames = computed(() => {
    const uploadedTypes = new Set(
      this.loaDocuments()
        .map((item) => this.normalizeDocToken(item.document_type))
        .filter((item) => !!item),
    );
    return this.activeLoaDocumentConfigs()
      .filter((item) => this.resolveDocumentMandatory(item))
      .map((item) => (item.doc_title || item.document_name || '').trim())
      .filter((name) => !!name && !uploadedTypes.has(this.normalizeDocToken(name)));
  });
  protected readonly hasUploadedLoaAgreement = computed(() =>
    this.loaDocuments().some((item) => this.normalizeDocToken(item.document_type) === 'LOA AGREEMENT'),
  );
  protected readonly loaUploadTypeOptions = computed(() => {
    const defaults = this.activeLoaDocumentConfigs()
      .map((item) => item.doc_title || item.document_name || '')
      .filter((item) => !!item?.trim())
      .filter((item) => !(this.hasUploadedLoaAgreement() && this.normalizeDocToken(item) === 'LOA AGREEMENT'));
    const unique = Array.from(new Set(defaults));
    return [...unique, 'Other'];
  });
  protected isSopBilling(): boolean {
    return this.billConfigForm.controls.bill_type.value === 'SOP';
  }

  protected isTmrBilling(): boolean {
    return this.billConfigForm.controls.bill_type.value === 'TMR';
  }

  protected isOaBilling(): boolean {
    return this.billConfigForm.controls.bill_type.value === 'OA';
  }

  protected isMcpBillingRateType(): boolean {
    return this.billConfigForm.controls.rate_type.value === 'MCP';
  }

  protected isSopMcpBilling(): boolean {
    return this.isSopBilling() && this.isMcpBillingRateType();
  }

  protected isRateValueLocked(): boolean {
    const cond = this.billConfigForm.controls.mcp_condition.value || '';
    return this.isSopMcpBilling() && (cond === 'Average' || cond === 'Equal');
  }

  protected readonly selectedBillCycle = computed(() => this.billCycleSelection());
  protected readonly billCycleOptionsForCurrentType = computed(() =>
    this.isOaBilling() ? this.oaBillCycleOptions : this.billCycleOptions,
  );
  protected readonly isCustomBillCycle = computed(() => this.selectedBillCycle() === 'Custom');
  billtypemodal: string = '';
  protected readonly billCycleViewLines = computed(() => {
    const cycle = this.selectedBillCycle();
    if (cycle === 'Weekly') return ['1-8', '9-15', '16-23', '24-last Day'];
    if (cycle === 'Monthly') return ['1-Last Day'];
    if (cycle === 'Fortnightly') return ['1-15', '16-Last Day'];
    if (cycle === 'Approval Wise') return ['Initial Approval', 'Accounts Approval', 'Operation Approval'];
    return [] as string[];
  });
  protected readonly cycleRangesForPreview = computed(() => {
    if (this.isCustomBillCycle()) return this.customCycleRanges();
    return this.billCycleViewLines().map((line, index) => {
      const [from, to] = line.split('-');
      return { id: `fixed-${index}`, from: from || '', to: to || '' };
    });
  });
  protected readonly canEditCustomRanges = computed(() => this.isCustomBillCycle());
  protected readonly invalidOaRows = computed(() =>
    this.oaChargeAllocations().filter((row) => Number(row.buyer_percent) + Number(row.seller_percent) > 100),
  );
  protected readonly penaltyApplicable = computed(() => this.penaltyEnabled());
  protected readonly isQuantumWisePenalty = computed(() => this.penaltyModeSelection() === 'quantum_wise');
  protected readonly sortedPenaltySlabs = computed(() =>
    [...this.penaltySlabs()].sort((a, b) => a.from_unit - b.from_unit || a.to_unit - b.to_unit),
  );

  protected isSellCategory(): boolean {
    return this.selectedCategory() === 'Sell';
  }

  protected canAddSchedule(direction?: 'import' | 'export'): boolean {
    if (this.readOnly()) return false;
    return this.canEditCurrentLoa();
  }

  protected canEditSchedule(item: LoaSchedule | null | undefined): boolean {
    if (this.readOnly()) return false;
    if (!item) return this.canEditCurrentLoa();
    if (item.can_edit !== null && item.can_edit !== undefined) return !!item.can_edit;
    return this.canEditCurrentLoa();
  }

  protected canDeleteSchedule(item: LoaSchedule | null | undefined): boolean {
    if (this.readOnly()) return false;
    if (!item) return this.canEditCurrentLoa();
    if (item.can_delete !== null && item.can_delete !== undefined) return !!item.can_delete;
    return this.canEditSchedule(item);
  }

  protected canEditExistingSchedules(): boolean {
    return !this.readOnly() && this.displaySchedules().some((item) => this.canEditSchedule(item) || this.canDeleteSchedule(item));
  }

  protected canAddEmd(): boolean {
    return !this.readOnly() && this.canEditCurrentLoa();
  }

  protected canEditEmd(item: LoaEmd | null | undefined): boolean {
    if (this.readOnly()) return false;
    if (!item) return this.canEditCurrentLoa();
    if (item.can_edit !== null && item.can_edit !== undefined) return !!item.can_edit;
    return this.canEditCurrentLoa();
  }

  protected canDeleteEmd(item: LoaEmd | null | undefined): boolean {
    if (this.readOnly()) return false;
    if (!item) return this.canEditCurrentLoa();
    if (item.can_delete !== null && item.can_delete !== undefined) return !!item.can_delete;
    return this.canEditEmd(item);
  }

  protected canEditExistingEmds(): boolean {
    return !this.readOnly() && this.displayEmds().some((item) => this.canEditEmd(item) || this.canDeleteEmd(item));
  }

  protected canEditBillingConfigs(): boolean {
    return !this.readOnly() && this.canEditCurrentLoa();
  }

  protected canEditBillConfigItem(item: LoaBillConfig | null | undefined): boolean {
    if (this.readOnly()) return false;
    if (!item) return this.canEditCurrentLoa();
    if (item.can_edit !== null && item.can_edit !== undefined) return !!item.can_edit;
    return this.canEditCurrentLoa();
  }

  protected canDeleteBillConfigItem(item: LoaBillConfig | null | undefined): boolean {
    if (this.readOnly()) return false;
    if (!item) return this.canEditCurrentLoa();
    if (item.can_delete !== null && item.can_delete !== undefined) return !!item.can_delete;
    return this.canEditBillConfigItem(item);
  }

  protected scheduleSectionTitle(): string {
    return this.isSellCategory() ? 'Export Scheduled' : 'Import Scheduled';
  }

  protected addScheduleButtonLabel(): string {
    return this.isSellCategory() ? 'Add Export Schedule' : 'Add Import Schedule';
  }

  private canUserEditLoa(loa: Loa | null | undefined): boolean {
    if (this.hideFinalApprovalForExternalView()) return false;
    if (!loa) return false;
    const userId = this.auth.user()?.id || "";
    const role = this.userRoleKey();
    const isSuperAdmin = role === 'super_admin';
    const isCreator = !!userId && (loa.created_by || "") === userId;
    return isSuperAdmin || isCreator;
  }

  protected hasBankingPriority(): boolean {
    return !!this.form.controls.banking_priority.value;
  }

  protected bankingScheduleOrder(): Array<'import' | 'export'> {
    const priority = (this.form.controls.banking_priority.value || '').toLowerCase();
    return priority === 'export' ? ['export', 'import'] : ['import', 'export'];
  }

  protected scheduleTitleByDirection(direction: 'import' | 'export'): string {
    return direction === 'import' ? 'Import Scheduled' : 'Export Scheduled';
  }

  protected addScheduleButtonLabelByDirection(direction: 'import' | 'export'): string {
    return direction === 'import' ? 'Add Import Schedule' : 'Add Export Schedule';
  }

  protected schedulesByDirection(direction: 'import' | 'export'): LoaSchedule[] {
    return this.displaySchedules()
      .filter((item) => this.normalizeScheduleDirection(item.direction, 'import') === direction)
      .sort((a, b) => this.scheduleArrangementValue(a) - this.scheduleArrangementValue(b));
  }

  protected isSecondBankingDirection(direction: 'import' | 'export'): boolean {
    const order = this.bankingScheduleOrder();
    return order.length > 1 && order[1] === direction;
  }

  protected scheduleModalTitle(): string {
    return this.activeScheduleDirection() === 'export' ? 'Export Scheduled' : 'Import Scheduled';
  }

  protected currentScheduleDirection(): 'import' | 'export' {
    if (this.isBanking()) return this.activeScheduleDirection();
    return this.normalizeScheduleDirection(this.scheduleForm.controls.direction.value, 'import');
  }

  protected orderedDisplaySchedules(): LoaSchedule[] {
    return [...this.displaySchedules()].sort((a, b) => this.scheduleArrangementValue(a) - this.scheduleArrangementValue(b));
  }

  protected arrangementOptions(direction: 'import' | 'export'): number[] {
    const used = this.displaySchedules()
      .filter((item) => this.normalizeScheduleDirection(item.direction, 'import') === direction)
      .map((item) => this.scheduleArrangementValue(item));
    const max = used.length ? Math.max(...used) : 0;
    const upperBound = Math.max(max + 1, 3);
    return Array.from({ length: upperBound }, (_, i) => i + 1);
  }

  protected onScheduleDirectionChange(value: string): void {
    const direction = (value || 'import') as 'import' | 'export';
    this.activeScheduleDirection.set(direction);
    const currentArrangement = this.asIntOrNull(this.scheduleForm.controls.arrangement.value);
    if (!currentArrangement || currentArrangement < 1) {
      this.scheduleForm.patchValue({ arrangement: this.nextArrangementForDirection(direction).toString() });
    }
  }

  constructor() {
    this.resetOaAllocationDefaults();
    this.penaltySlabs.set([]);
    this.penaltyEnabled.set(false);
    this.penaltyModeSelection.set('fixed_rate');
    this.draftDocumentConfigs.set([]);
    this.loadDocumentConfigs();
    this.loadEnergyTypeOptions();
    this.vendorsService
      .list()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((vendors) => {
        if (this.isVendorUser()) {
          const vendorId = this.auth.user()?.vendor_id;
          this.vendors.set((vendors || []).filter((vendor) => vendor.id === vendorId));
          this.syncEnergyTypeFromVendor(this.form.controls.vendor_id.value);
          return;
        }
        this.vendors.set(vendors);
        this.syncEnergyTypeFromVendor(this.form.controls.vendor_id.value);
      });
    this.form.controls.vendor_id.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((vendorId) => {
      if (vendorId) this.loadLoaDocuments(vendorId, this.loaId());
      else this.loaDocuments.set([]);
      this.syncEnergyTypeFromVendor(vendorId);
    });
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const id = params.get('id');
      const url = this.router.url;
      const mode: Mode = this.isVendorUser()
        ? (id ? 'view' : 'new')
        : id
          ? (url.includes('/edit') ? 'edit' : 'view')
          : 'new';
      this.mode.set(mode);
      this.loaId.set(id);
      if (!id) {
        this.currentLoa.set(null);
        this.penaltySlabs.set([]);
        this.penaltyEnabled.set(false);
        this.penaltyModeSelection.set('fixed_rate');
      }
      if (!id && this.isVendorUser()) {
        this.toast.show('Vendor users can only view their own LOAs', '', 'info');
        this.router.navigateByUrl('/loas');
        return;
      }
      if (!id) {
        const category = this.route.snapshot.queryParamMap.get('category');
        if (category) {
          this.form.patchValue({ category: this.normalizeLoaCategory(category) });
        }
      }
      if (id) {
        this.loadLoa(id);
        this.loadSubModules(id);
      }
      if (mode === 'view') {
        this.form.disable({ emitEvent: false });
      } else {
        this.form.enable({ emitEvent: false });
      }
    });
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      this.viewSource.set((params.get('source') || 'loa').trim().toLowerCase() || 'loa');
    });
  }

  private loadLoa(id: string): void {
    this.loading.set(true);
    this.loaService.get(id).subscribe({
      next: (loa) => {
        this.currentLoa.set(loa);
        const canEditLoa = loa.can_edit !== null && loa.can_edit !== undefined ? !!loa.can_edit : this.canUserEditLoa(loa);
        if (this.mode() === 'edit' && !canEditLoa) {
          this.form.disable({ emitEvent: false });
          this.mode.set('view');
          this.loading.set(false);
          this.toast.show('Only super admin or the LOA creator can edit this LOA', '', 'danger');
          this.router.navigateByUrl('/loas/' + loa.id);
          return;
        }
        this.form.patchValue({
          loa_number: loa.loa_number || '',
          vendor_id: loa.vendor_id || '',
          category: this.normalizeLoaCategory(loa.category),
          loa_term: loa.loa_term || '',
          issue_date: loa.issue_date || '',
          effective_from: loa.effective_from || '',
          effective_to: loa.effective_to || '',
          loa_lead: loa.loa_lead || '',
          is_provisional: !!loa.is_provisional,
          energy_type: loa.energy_type || '',
          delivery_point: loa.delivery_point || '',
          state: loa.state || '',
          rate_type: loa.rate_type || '',
          mcp_condition: loa.mcp_condition || '',
          mcp_value: loa.mcp_value?.toString() || '',
          fixed_rate: loa.fixed_rate?.toString() || '',
          penalty_applicable: !!loa.penalty_applicable,
          penalty_mode: loa.penalty_mode || 'fixed_rate',
          penalty_fixed_rate: loa.penalty_fixed_rate?.toString() || '',
          banking_priority: loa.banking_priority || '',
          revision_limit: loa.revision_limit?.toString() || '',
          remarks: loa.remarks || '',
          status: loa.loa_status || loa.status || 'draft',
        });
        this.penaltyEnabled.set(!!loa.penalty_applicable);
        this.penaltyModeSelection.set((loa.penalty_mode === 'quantum_wise' ? 'quantum_wise' : 'fixed_rate'));
        this.penaltySlabs.set(this.parsePenaltySlabs(loa.penalty_slabs_json));
        if (loa.vendor_id) this.loadLoaDocuments(loa.vendor_id, loa.id);
        this.loading.set(false);
      },
      error: () => {
        this.currentLoa.set(null);
        this.penaltySlabs.set([]);
        this.penaltyEnabled.set(false);
        this.penaltyModeSelection.set('fixed_rate');
        this.loading.set(false);
        this.toast.show('LOA not found', '', 'danger');
        this.router.navigateByUrl('/loas');
      },
    });
  }

  private loadSubModules(loaId: string): void {
    this.loaService.listSchedules(loaId).subscribe((items) => this.schedules.set(items));
    this.loaService.listEmds(loaId).subscribe((items) => this.emds.set(items));
    this.loaService.listBillConfigs(loaId).subscribe((items) => {
      this.billConfigs.set(items);
      this.syncBillingRunsFromConfigs(items);
    });
  }

  private loadDocumentConfigs(): void {
    this.loaService.listDocumentConfigs().subscribe({
      next: (items) => {
        const loaItems = items.filter((item) => {
          const moduleName = (item.module_name || '').toUpperCase();
          const status = (item.status || 'Active').toUpperCase();
          return (moduleName === 'LOA MODULE' || moduleName === 'LOA') && status === 'ACTIVE';
        });
        this.documentConfigs.set(loaItems);
      },
      error: () => {
        this.documentConfigs.set([]);
      },
    });
  }

  private loadEnergyTypeOptions(): void {
    this.loaService.listEnergyTypes().subscribe({
      next: (response) => {
        const options = Array.from(
          new Set((response?.options || []).map((item) => this.normalizeEnergyTypeValue(item)).filter((item) => !!item)),
        );
        this.energyTypeOptions.set(options);
      },
      error: () => {
        this.energyTypeOptions.set([]);
      },
    });
  }

  private loadLoaDocuments(vendorId: string, loaId?: string | null): void {
    this.documentsService.listVendorDocuments(vendorId, loaId, !loaId).subscribe({
      next: (items) => this.loaDocuments.set(items),
      error: () => this.loaDocuments.set([]),
    });
  }

  private syncEnergyTypeFromVendor(vendorId: string | null | undefined): void {
    if (this.mode() === 'view') return;
    const currentLoaVendorId = this.currentLoa()?.vendor_id || '';
    const isSameAsLoadedLoaVendor = !!vendorId && vendorId === currentLoaVendorId;
    const vendor = this.vendors().find((item) => item.id === vendorId);
    const vendorType = (vendor?.vendor_type || '').trim().toLowerCase();
    if (vendorType !== 'generator') {
      if (!(this.mode() === 'edit' && isSameAsLoadedLoaVendor)) {
        this.form.patchValue({ energy_type: '' }, { emitEvent: false });
      }
      return;
    }
    if (this.mode() === 'edit' && isSameAsLoadedLoaVendor && (this.form.controls.energy_type.value || '').trim()) return;
    const energyType = this.normalizeEnergyTypeValue(vendor?.generator_type);
    this.form.patchValue({ energy_type: energyType }, { emitEvent: false });
  }

  private normalizeEnergyTypeValue(value: string | null | undefined): string {
    const normalized = (value || '').trim();
    if (!normalized) return '';
    const exact = this.energyTypeOptions().find((item) => item.toLowerCase() === normalized.toLowerCase());
    if (exact) return exact;
    return normalized
      .split(/\s+/)
      .map((item) => item.charAt(0).toUpperCase() + item.slice(1).toLowerCase())
      .join(' ');
  }

  protected submit(): void {
    if (this.isVendorUser()) {
      this.toast.show('Vendor users have read-only access to LOA module', '', 'danger');
      return;
    }
    if (this.form.invalid || this.mode() === 'view') return;
    const validationError = this.validateLoaBusinessRules('submit');
    if (validationError) {
      this.toast.show(validationError, '', 'danger');
      return;
    }
    this.loading.set(true);
    const payload = this.buildLoaPayload(this.form.getRawValue());

    const id = this.loaId();
    const req$ = id ? this.loaService.update(id, payload) : this.loaService.createWorkflow(payload);
    req$.subscribe({
      next: (loa) => {
        if (!id) {
          this.persistDraftChildren(loa.id).subscribe({
            next: () => {
              this.loading.set(false);
              this.toast.show('LOA submitted for approval', '', 'success');
              this.router.navigateByUrl('/loas');
            },
            error: () => {
              this.loading.set(false);
              this.toast.show('LOA saved, but child sections failed to save', '', 'danger');
              this.router.navigateByUrl(`/loas/${loa.id}/edit`);
            },
          });
          return;
        }
        this.loading.set(false);
        this.toast.show('LOA updated', '', 'success');
        if (id) {
          this.loadSubModules(id);
        }
      },
      error: () => {
        this.loading.set(false);
        this.toast.show('Failed to save LOA', '', 'danger');
      },
    });
  }

  protected approveLoa(): void {
    const loaId = this.loaId();
    if (!loaId || this.loading()) return;
    const validationError = this.validateLoaBusinessRules('approve');
    if (validationError) {
      this.toast.show(validationError, '', 'danger');
      return;
    }
    const isInitialStage = this.canInitialApprove();
    const isAccountsStage = this.canAccountsApprove();
    const approve$ = isInitialStage
      ? this.loaService.initialApprove(loaId)
      : isAccountsStage
        ? this.loaService.accountsApprove(loaId)
        : this.loaService.operationApprove(loaId);
    this.loading.set(true);
    approve$.subscribe({
      next: (loa) => {
        this.loading.set(false);
        this.currentLoa.set(loa);
        this.form.patchValue({ status: loa.loa_status || loa.status || 'approved' });
        this.toast.show(
          isInitialStage ? 'Initial approval completed' : isAccountsStage ? 'Accounts approval completed' : 'Operation approval completed',
          '',
          'success',
        );
      },
      error: () => {
        this.loading.set(false);
        this.toast.show('Failed to approve LOA', '', 'danger');
      },
    });
  }

  protected rejectLoa(): void {
    const loaId = this.loaId();
    if (!loaId || this.loading()) return;
    const remarks = (this.form.controls.remarks.value || '').trim();
    if (!remarks) {
      this.toast.show('Remarks are required to reject LOA', '', 'danger');
      return;
    }
    const isInitialStage = this.canInitialApprove();
    const isAccountsStage = this.canAccountsApprove();
    const reject$ = isInitialStage
      ? this.loaService.initialReject(loaId, remarks)
      : isAccountsStage
        ? this.loaService.accountsReject(loaId, remarks)
        : this.loaService.operationReject(loaId, remarks);
    this.loading.set(true);
    reject$.subscribe({
      next: (loa) => {
        this.loading.set(false);
        this.currentLoa.set(loa);
        this.form.patchValue({ status: loa.loa_status || loa.status || 'rejected' });
        this.toast.show('LOA rejected', '', 'success');
      },
      error: () => {
        this.loading.set(false);
        this.toast.show('Failed to reject LOA', '', 'danger');
      },
    });
  }

  private buildLoaPayload(formValue: ReturnType<typeof this.form.getRawValue>): Partial<Loa> {
    const selectedRuns = this.billingRuns();
    const selectedSettlementBillType = selectedRuns.SOP ? 'SOP' : selectedRuns.TMR ? 'TMR' : null;
    console.log('selectedSettlementBillType',selectedSettlementBillType)
    return {
      loa_number: formValue.loa_number,
      vendor_id: formValue.vendor_id,
      category: this.normalizeLoaCategory(formValue.category),
      loa_term: this.asNullable(formValue.loa_term),
      issue_date: formValue.issue_date,
      effective_from: this.asNullable(formValue.effective_from),
      effective_to: this.asNullable(formValue.effective_to),
      loa_lead: this.asNullable(formValue.loa_lead),
      is_provisional: formValue.is_provisional,
      energy_type: this.asNullable(formValue.energy_type),
      delivery_point: this.asNullable(formValue.delivery_point),
      state: this.asNullable(formValue.state),
      rate_type: this.asNullable(formValue.rate_type),
      mcp_condition: this.asNullable(formValue.mcp_condition),
      mcp_value: this.asNumberOrNull(formValue.mcp_value),
      fixed_rate: this.asNumberOrNull(formValue.fixed_rate),
      penalty_applicable: !!formValue.penalty_applicable,
      penalty_mode: formValue.penalty_applicable ? this.asNullable(formValue.penalty_mode) : null,
      penalty_fixed_rate:
        formValue.penalty_applicable && formValue.penalty_mode === 'fixed_rate'
          ? this.asNumberOrNull(formValue.penalty_fixed_rate)
          : null,
      penalty_slabs_json:
        formValue.penalty_applicable && formValue.penalty_mode === 'quantum_wise'
          ? this.serializePenaltySlabs(this.penaltySlabs())
          : null,
      banking_priority: this.asNullable(formValue.banking_priority),
      selected_settlement_bill_type: selectedSettlementBillType,
      revision_limit: this.asNumberOrNull(formValue.revision_limit),
      remarks: this.asNullable(formValue.remarks),
      status: formValue.status,
    };
  }

  protected setPenaltyApplicable(checked: boolean): void {
    this.penaltyEnabled.set(checked);
    this.form.patchValue({ penalty_applicable: checked });
  }

  protected setPenaltyMode(mode: string): void {
    const nextMode: 'fixed_rate' | 'quantum_wise' = mode === 'quantum_wise' ? 'quantum_wise' : 'fixed_rate';
    this.penaltyModeSelection.set(nextMode);
    this.form.patchValue({ penalty_mode: nextMode });
  }

  protected editPenaltySlab(row?: PenaltyQuantumSlab | null): void {
    if (this.readOnly()) return;
    if (!row) {
      this.editingPenaltySlabId.set(null);
      this.penaltySlabForm.reset({ from_unit: '', to_unit: '', rate: '' });
      return;
    }
    this.editingPenaltySlabId.set(row.id);
    this.penaltySlabForm.patchValue({
      from_unit: row.from_unit.toString(),
      to_unit: row.to_unit.toString(),
      rate: row.rate.toString(),
    });
  }

  protected cancelPenaltySlab(): void {
    this.editingPenaltySlabId.set(null);
    this.penaltySlabForm.reset({ from_unit: '', to_unit: '', rate: '' });
  }

  protected savePenaltySlab(): void {
    if (this.readOnly()) return;
    const formValue = this.penaltySlabForm.getRawValue();
    const fromUnit = this.asNumberOrNull(formValue.from_unit);
    const toUnit = this.asNumberOrNull(formValue.to_unit);
    const rate = this.asNumberOrNull(formValue.rate);
    if (fromUnit === null || toUnit === null || rate === null) {
      this.toast.show('Penalty slab requires From, To, and Rate', '', 'danger');
      return;
    }
    if (fromUnit < 0 || toUnit < fromUnit) {
      this.toast.show('Penalty slab range is invalid', '', 'danger');
      return;
    }
    if (rate < 0) {
      this.toast.show('Penalty rate cannot be negative', '', 'danger');
      return;
    }
    const editingId = this.editingPenaltySlabId();
    const nextRow: PenaltyQuantumSlab = {
      id: editingId || this.tempId('penalty'),
      from_unit: fromUnit,
      to_unit: toUnit,
      rate,
    };
    const overlap = this.penaltySlabs().some((row) => {
      if (row.id === editingId) return false;
      return Math.max(row.from_unit, fromUnit) <= Math.min(row.to_unit, toUnit);
    });
    if (overlap) {
      this.toast.show('Penalty slab range overlaps with an existing row', '', 'danger');
      return;
    }
    const nextRows = editingId
      ? this.penaltySlabs().map((row) => (row.id === editingId ? nextRow : row))
      : [...this.penaltySlabs(), nextRow];
    this.penaltySlabs.set(nextRows);
    this.cancelPenaltySlab();
  }

  protected removePenaltySlab(id: string): void {
    if (this.readOnly()) return;
    this.penaltySlabs.set(this.penaltySlabs().filter((row) => row.id !== id));
    if (this.editingPenaltySlabId() === id) {
      this.cancelPenaltySlab();
    }
  }

  protected editSchedule(item: LoaSchedule | null, direction?: 'import' | 'export'): void {
    if (item && !this.canEditSchedule(item)) {
      this.toast.show('Schedule editing is not allowed for this LOA', '', 'info');
      return;
    }
    this.showScheduleForm.set(true);
    const defaultDirection: 'import' | 'export' = direction || (this.isSellCategory() ? 'export' : 'import');
    this.activeScheduleDirection.set(defaultDirection);
    if (!item) {
      this.editingScheduleId.set(null);
      this.scheduleForm.reset({
        from_date: '',
        to_date: '',
        from_time: '',
        to_time: '',
        quantum_mw: '',
        return_percentage: '',
        arrangement: this.nextArrangementForDirection(defaultDirection).toString(),
        direction: defaultDirection,
      });
      return;
    }
    this.editingScheduleId.set(item.id);
    const itemDirection = this.normalizeScheduleDirection(item.direction, defaultDirection);
    this.activeScheduleDirection.set(itemDirection);
    this.scheduleForm.patchValue({
      from_date: item.from_date || '',
      to_date: item.to_date || '',
      from_time: this.normalizeScheduleTime(item.from_time, false),
      to_time: this.normalizeScheduleTime(item.to_time, true),
      quantum_mw: item.quantum_mw?.toString() || '',
      return_percentage: item.return_percentage?.toString() || '',
      arrangement: this.scheduleArrangementValue(item).toString(),
      direction: itemDirection,
    });
  }

  protected cancelSchedule(): void {
    this.showScheduleForm.set(false);
    this.editingScheduleId.set(null);
  }

  protected saveSchedule(): void {
    if (this.scheduleForm.invalid) return;
    const fromTime = this.scheduleForm.controls.from_time.value;
    const toTime = this.scheduleForm.controls.to_time.value;
    if (!this.isQuarterHourTime(fromTime, false) || !this.isQuarterHourTime(toTime, true)) {
      this.toast.show('Use HH:MM in 15-minute intervals. To Time can be 24:00.', '', 'danger');
      return;
    }
    const direction = this.isBanking()
      ? this.activeScheduleDirection()
      : ((this.scheduleForm.controls.direction.value || 'import') as 'import' | 'export');
    const allowReturnPercentage = !this.isBanking() || this.isSecondBankingDirection(direction);
    const arrangement = this.asIntOrNull(this.scheduleForm.controls.arrangement.value);
    if (!arrangement || arrangement < 1) {
      this.toast.show('Arrangement must be a positive number', '', 'danger');
      return;
    }
    const editId = this.editingScheduleId();
    if (editId) {
      const currentItem = this.displaySchedules().find((item) => item.id === editId);
      if (!this.canEditSchedule(currentItem)) {
        this.toast.show('Schedule editing is not allowed for this LOA', '', 'danger');
        return;
      }
    } else if (!this.canAddSchedule(direction)) {
      this.toast.show('Schedule creation is not allowed for this LOA', '', 'danger');
      return;
    }
    const duplicateArrangement = this.displaySchedules().some(
      (item) =>
        item.id !== editId &&
        this.normalizeScheduleDirection(item.direction, 'import') === direction &&
        this.scheduleArrangementValue(item) === arrangement,
    );
    if (duplicateArrangement) {
      this.toast.show(`Arrangement ${arrangement} already exists for ${direction} schedule`, '', 'danger');
      return;
    }
    const payload = {
      id: editId || this.tempId('schedule'),
      loa_id: this.loaId() || '',
      ...this.scheduleForm.getRawValue(),
      direction,
      arrangement,
      quantum_mw: this.asNumberOrNull(this.scheduleForm.controls.quantum_mw.value),
      return_percentage: allowReturnPercentage ? this.asNumberOrNull(this.scheduleForm.controls.return_percentage.value) : null,
    } as LoaSchedule;

    if (!this.loaId()) {
      if (editId) {
        this.draftSchedules.set(this.draftSchedules().map((item) => (item.id === editId ? payload : item)));
      } else {
        this.draftSchedules.set([...this.draftSchedules(), payload]);
      }
      this.toast.show(editId ? 'Schedule updated' : 'Schedule added', '', 'success');
      this.cancelSchedule();
      return;
    }

    const req$ = editId
      ? this.loaService.updateSchedule(editId, payload as Partial<LoaSchedule>)
      : this.loaService.createSchedule(this.loaId()!, payload as Partial<LoaSchedule>);
    req$.subscribe({
      next: () => {
        this.toast.show(editId ? 'Schedule updated' : 'Schedule added', '', 'success');
        this.cancelSchedule();
        this.loadSubModules(this.loaId()!);
      },
      error: () => this.toast.show('Failed to save schedule', '', 'danger'),
    });
  }

  protected removeSchedule(id: string): void {
    const item = this.displaySchedules().find((row) => row.id === id);
    if (!this.canDeleteSchedule(item)) {
      this.toast.show('Schedule deletion is not allowed for this LOA', '', 'info');
      return;
    }
    if (!confirm('Delete this schedule?')) return;
    if (!this.loaId()) {
      this.draftSchedules.set(this.draftSchedules().filter((item) => item.id !== id));
      this.toast.show('Schedule deleted', '', 'success');
      return;
    }
    this.loaService.deleteSchedule(id).subscribe({
      next: () => {
        this.toast.show('Schedule deleted', '', 'success');
        if (this.loaId()) this.loadSubModules(this.loaId()!);
      },
      error: () => this.toast.show('Failed to delete schedule', '', 'danger'),
    });
  }

  protected editEmd(item: LoaEmd | null): void {
    if (item && !this.canEditEmd(item)) {
      this.toast.show('Security deposit editing is not allowed for this LOA', '', 'info');
      return;
    }
    this.showEmdForm.set(true);
    if (!item) {
      this.editingEmdId.set(null);
      this.emdForm.reset({
        security_deposit_type: '',
        issue_date: '',
        effective_from: '',
        effective_to: '',
        bank_name: '',
        reference_number: '',
        amount: '',
      });
      return;
    }
    this.editingEmdId.set(item.id);
    this.emdForm.patchValue({
      security_deposit_type: item.security_deposit_type,
      issue_date: item.issue_date,
      effective_from: item.effective_from,
      effective_to: item.effective_to,
      bank_name: item.bank_name,
      reference_number: item.reference_number,
      amount: item.amount?.toString() || '',
    });
  }

  protected cancelEmd(): void {
    this.showEmdForm.set(false);
    this.editingEmdId.set(null);
  }

  protected saveEmd(): void {
    if (this.emdForm.invalid) return;
    const editId = this.editingEmdId();
    if (editId) {
      const currentItem = this.displayEmds().find((item) => item.id === editId);
      if (!this.canEditEmd(currentItem)) {
        this.toast.show('Security deposit editing is not allowed for this LOA', '', 'danger');
        return;
      }
    } else if (!this.canAddEmd()) {
      this.toast.show('Security deposit creation is not allowed for this LOA', '', 'danger');
      return;
    }
    const payload = {
      id: editId || this.tempId('emd'),
      loa_id: this.loaId() || '',
      ...this.emdForm.getRawValue(),
      amount: this.asNumberOrNull(this.emdForm.controls.amount.value),
    } as LoaEmd;

    if (!this.loaId()) {
      if (editId) {
        this.draftEmds.set(this.draftEmds().map((item) => (item.id === editId ? payload : item)));
      } else {
        this.draftEmds.set([...this.draftEmds(), payload]);
      }
      this.toast.show(editId ? 'EMD updated' : 'EMD added', '', 'success');
      this.cancelEmd();
      return;
    }

    const req$ = editId ? this.loaService.updateEmd(editId, payload as Partial<LoaEmd>) : this.loaService.createEmd(this.loaId()!, payload as Partial<LoaEmd>);
    req$.subscribe({
      next: () => {
        this.toast.show(editId ? 'EMD updated' : 'EMD added', '', 'success');
        this.cancelEmd();
        this.loadSubModules(this.loaId()!);
      },
      error: () => this.toast.show('Failed to save EMD', '', 'danger'),
    });
  }

  protected removeEmd(id: string): void {
    const item = this.displayEmds().find((row) => row.id === id);
    if (!this.canDeleteEmd(item)) {
      this.toast.show('Security deposit deletion is not allowed for this LOA', '', 'info');
      return;
    }
    if (!confirm('Delete this EMD record?')) return;
    if (!this.loaId()) {
      this.draftEmds.set(this.draftEmds().filter((item) => item.id !== id));
      this.toast.show('EMD deleted', '', 'success');
      return;
    }
    this.loaService.deleteEmd(id).subscribe({
      next: () => {
        this.toast.show('EMD deleted', '', 'success');
        if (this.loaId()) this.loadSubModules(this.loaId()!);
      },
      error: () => this.toast.show('Failed to delete EMD', '', 'danger'),
    });
  }

  protected editBillConfig(item: LoaBillConfig | null): void {
    // if (item && this.isApprovedLoa()) {
    //   this.toast.show('Billing configuration cannot be edited after LOA approval', '', 'info');
    //   return;
    // }
    this.showBillForm.set(true);
    if (!item) {
      this.billtypemodal = 'SOP';
      this.editingBillConfigId.set(null);
      this.billConfigForm.reset({
        bill_type: 'SOP',
        bill_cycle: '',
        effective_date: '',
        rate_type: 'MCP',
        
        mcp_condition: '',
        mcp_rate: '',
        tmr_rate: '',
        rate: '',
        due_days: '',
        rebate: '',
        rebate_days: '',
        discount_price: '',
        custom_bill_cycle_view: '',
      });
      this.billCycleSelection.set('');
      this.applyBillTypeFieldRules('SOP');
      this.syncSopMcpRateBehavior();
      return;
    }
    this.billtypemodal = item.bill_type || 'SOP';
    this.editingBillConfigId.set(item.id);
    this.billConfigForm.patchValue({
      bill_type: item.bill_type || 'SOP',
      bill_cycle: item.bill_cycle || '',
      effective_date: item.effective_date || '',
      rate_type: item.rate_type || 'MCP',
      
      mcp_condition: item.mcp_condition || '',
      mcp_rate: item.mcp_rate?.toString() || '',
      tmr_rate: item.tmr_rate?.toString() || '',
      rate: item.rate?.toString() || '',
      due_days: item.due_days?.toString() || '',
      rebate: item.rebate?.toString() || '',
      rebate_days: item.rebate_days?.toString() || '',
      discount_price: item.discount_price?.toString() || '',
      custom_bill_cycle_view: item.bill_cycle?.startsWith('Custom:') ? item.bill_cycle.replace('Custom:', '') : '',
    });
    this.billCycleSelection.set(item.bill_cycle || '');
    this.applyBillTypeFieldRules(this.billtypemodal as 'SOP' | 'TMR' | 'OA');
    this.syncSopMcpRateBehavior();
    if ((item.bill_type || 'SOP') === 'OA') {
      this.oaChargeAllocations.set(this.parseOaChargeAllocation(item.oa_charge_allocation));
    } else {
      this.resetOaAllocationDefaults();
    }
  }

  protected cancelBillConfig(): void {
    this.showBillForm.set(false);
    this.editingBillConfigId.set(null);
  }

  protected saveBillConfig(): void {
    if (!this.validateBillingConfig()) return;
    const payload = {
      id: this.editingBillConfigId() || this.tempId('bill'),
      loa_id: this.loaId() || '',
      ...this.billConfigForm.getRawValue(),
      bill_cycle: this.resolveBillCycleValue(),
      effective_date: this.asNullable(this.billConfigForm.controls.effective_date.value),
      rate_type: this.asNullable(this.billConfigForm.controls.rate_type.value),
      mcp_rate: this.asNumberOrNull(this.billConfigForm.controls.mcp_rate.value),
      tmr_rate: this.asNumberOrNull(this.billConfigForm.controls.tmr_rate.value),
      rate: this.asNumberOrNull(this.billConfigForm.controls.rate.value),
      due_days: this.asIntOrNull(this.billConfigForm.controls.due_days.value),
      rebate: this.asNumberOrNull(this.billConfigForm.controls.rebate.value),
      rebate_days: this.asIntOrNull(this.billConfigForm.controls.rebate_days.value),
      discount_price: this.asNumberOrNull(this.billConfigForm.controls.discount_price.value),
      oa_charge_allocation:
        (this.billConfigForm.controls.bill_type.value as 'SOP' | 'TMR' | 'OA') === 'OA'
          ? JSON.stringify(this.oaChargeAllocations())
          : null,
    } as LoaBillConfig;

    if (!this.loaId()) {
      const editId = this.editingBillConfigId();
      if (editId) {
        this.draftBillConfigs.set(this.draftBillConfigs().map((item) => (item.id === editId ? payload : item)));
      } else {
        this.draftBillConfigs.set([...this.draftBillConfigs(), payload]);
      }
      this.toast.show(editId ? 'Bill config updated' : 'Bill config added', '', 'success');
      this.cancelBillConfig();
      return;
    }

    const editId = this.editingBillConfigId();
    const req$ = editId
      ? this.loaService.updateBillConfig(editId, payload as Partial<LoaBillConfig>)
      : this.loaService.createBillConfig(this.loaId()!, payload as Partial<LoaBillConfig>);
    req$.subscribe({
      next: () => {
        this.toast.show(editId ? 'Bill config updated' : 'Bill config added', '', 'success');
        this.cancelBillConfig();
        this.loadSubModules(this.loaId()!);
      },
      error: () => this.toast.show('Failed to save bill config', '', 'danger'),
    });
  }

  protected removeBillConfig(id: string): void {
    const item = this.displayBillConfigs().find((row) => row.id === id);
    if (!this.canDeleteBillConfigItem(item)) {
      this.toast.show('Billing configuration deletion is not allowed for this LOA', '', 'info');
      return;
    }
    if (!confirm('Delete this bill config?')) return;
    if (!this.loaId()) {
      this.draftBillConfigs.set(this.draftBillConfigs().filter((item) => item.id !== id));
      this.toast.show('Bill config deleted', '', 'success');
      return;
    }
    this.loaService.deleteBillConfig(id).subscribe({
      next: () => {
        this.toast.show('Bill config deleted', '', 'success');
        if (this.loaId()) this.loadSubModules(this.loaId()!);
      },
      error: () => this.toast.show('Failed to delete bill config', '', 'danger'),
    });
  }

  protected toggleBillingRun(type: 'SOP' | 'TMR' | 'OA', checked: boolean): void {
    if (this.mode() === 'view') return;
    const current = this.billingRuns();
    if (type === 'OA') {
      this.billingRuns.set({ ...current, OA: checked });
      return;
    }
    if (type === 'SOP') {
      this.billingRuns.set({
        ...current,
        SOP: checked,
        TMR: checked ? false : current.TMR,
      });
      return;
    }
    this.billingRuns.set({
      ...current,
      TMR: checked,
      SOP: checked ? false : current.SOP,
    });
  }

  protected hasBillingConfig(type: 'SOP' | 'TMR' | 'OA'): boolean {
    return this.displayBillConfigs().some((item) => item.bill_type === type);
  }

  protected billingConfigsFor(type: 'SOP' | 'TMR' | 'OA'): LoaBillConfig[] {
    return this.displayBillConfigs()
      .filter((item) => item.bill_type === type)
      .sort((a, b) => (b.effective_date || '').localeCompare(a.effective_date || ''));
  }

  protected billingConfigFor(type: 'SOP' | 'TMR' | 'OA'): LoaBillConfig | null {
    const items = this.billingConfigsFor(type);
    if (!items.length) return null;
    return items[0];
  }

  protected billingCycleDisplay(value: string | null | undefined): string {
    if (!value) return '-';
    if (value.startsWith('Custom:')) return 'Custom';
    return value;
  }

  protected billingRateTypeDisplay(value: string | null | undefined): string {
    if (!value) return '-';
    const normalized = value.toUpperCase();
    return normalized === 'MCP' ? 'IEX-MCP' : normalized;
  }

  protected formatDisplayDate(value: string | null | undefined): string {
    if (!value) return '-';
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return value;
    const [, year, month, day] = match;
    return `${day}-${month}-${year}`;
  }

  protected billingFieldDisplay(value: string | number | null | undefined): string {
    if (value === null || value === undefined || value === '') return '-';
    return String(value);
  }

  protected billingCurrencyDisplay(value: string | number | null | undefined): string {
    if (value === null || value === undefined || value === '') return '-';
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return `₹ ${String(value)}`;
    return `₹ ${parsed.toFixed(2)}`;
  }

  protected openOaChargesView(config: LoaBillConfig): void {
    this.oaChargeViewRows.set(this.parseOaChargeAllocation(config.oa_charge_allocation));
    this.showOaChargesViewModal.set(true);
  }

  protected closeOaChargesView(): void {
    this.showOaChargesViewModal.set(false);
    this.oaChargeViewRows.set([]);
  }

  protected openAddBilling(type: 'SOP' | 'TMR' | 'OA'): void {
    if (this.mode() === 'view') return;
    this.billtypemodal = type
    this.editingBillConfigId.set(null);
    this.customCycleRanges.set([]);
    this.editingCycleRangeId.set(null);
    this.rangeEditor.reset({ from: '', to: '' });
    this.resetOaAllocationDefaults();
    this.billConfigForm.reset({
      bill_type: type,
      bill_cycle: '',
      effective_date: '',
        rate_type: 'MCP',
       
        
        mcp_condition: '',
        mcp_rate: '',
        tmr_rate: '',
        rate: '',
        due_days: '',
        rebate: '',
        rebate_days: '',
        discount_price: '',
        custom_bill_cycle_view: '',
      });
    this.showBillForm.set(true);
    this.billCycleSelection.set('');
    this.applyBillTypeFieldRules(type);
    this.syncSopMcpRateBehavior();
  }

  protected addLoaDocument(preferredType?: string): void {
    if (!this.canManageLoaUploads()) {
      this.toast.show('Upload/Re-upload is allowed only for draft, pending, or approved LOA', '', 'info');
      return;
    }
    if (!this.form.controls.vendor_id.value) {
      this.toast.show('Select Entity first to upload LOA documents', '', 'info');
      return;
    }
    const options = this.loaUploadTypeOptions();
    const preferred = (preferredType || '').trim();
    if (preferred && this.hasUploadedLoaAgreement() && this.normalizeDocToken(preferred) === 'LOA AGREEMENT') {
      this.toast.show('LOA Agreement is already uploaded. Please select another document type from the dropdown.', '', 'danger');
      return;
    }
    if (preferred && options.includes(preferred)) {
      this.selectedUploadDocType.set(preferred);
      this.selectedUploadOtherDocType.set('');
    } else if (preferred) {
      this.selectedUploadDocType.set('Other');
      this.selectedUploadOtherDocType.set(preferred);
    } else {
      this.selectedUploadDocType.set(options[0] || 'Other');
      this.selectedUploadOtherDocType.set('');
    }
    this.selectedUploadFiles.set([]);
    this.showDocumentUploadForm.set(true);
  }

  protected applyDefaultDocumentConfigs(): void {}

  protected editDocumentConfig(item: DocumentConfig | null): void {
    this.showDocumentForm.set(true);
    if (!item) {
      this.editingDocumentConfigId.set(null);
      this.documentForm.reset({
        module_name: 'LOA Module',
        client_types: 'Buy, Sell, Banking',
        doc_title: '',
        allowed_file_types: 'PDF',
        mandatory: false,
        max_file_size_mb: 10,
        status: 'Active',
        effective_date: '',
      });
      return;
    }
    this.editingDocumentConfigId.set(item.id);
    this.documentForm.patchValue({
      module_name: item.module_name || 'LOA Module',
      client_types: item.client_types || 'Buy, Sell, Banking',
      doc_title: item.doc_title || '',
      allowed_file_types: item.allowed_file_types || item.file_type || 'PDF',
      mandatory: this.resolveDocumentMandatory(item),
      max_file_size_mb: this.resolveMaxFileSizeMb(item),
      status: item.status || 'Active',
      effective_date: item.effective_date || '',
    });
  }

  protected cancelDocumentConfig(): void {
    this.showDocumentForm.set(false);
    this.editingDocumentConfigId.set(null);
  }

  protected saveDocumentConfig(): void {
    if (this.documentForm.invalid) return;
    const docTitle = this.documentForm.controls.doc_title.value.trim();
    const fileTypes = this.documentForm.controls.allowed_file_types.value.trim().toUpperCase();
    const clientTypes = this.documentForm.controls.client_types.value.trim();
    const maxFileSize = Number(this.documentForm.controls.max_file_size_mb.value || 0);
    if (!docTitle) {
      this.toast.show('Document title is required', '', 'danger');
      return;
    }
    if (!clientTypes) {
      this.toast.show('Client types are required', '', 'danger');
      return;
    }
    if (!fileTypes) {
      this.toast.show('Allowed file types are required', '', 'danger');
      return;
    }
    if (!Number.isFinite(maxFileSize) || maxFileSize <= 0) {
      this.toast.show('Max file size must be greater than 0', '', 'danger');
      return;
    }
    const payload: Partial<DocumentConfig> = {
      module_name: this.documentForm.controls.module_name.value || 'LOA Module',
      client_type: 'LOA',
      client_types: clientTypes,
      doc_title: docTitle,
      file_type: fileTypes,
      allowed_file_types: fileTypes,
      mandatory: !!this.documentForm.controls.mandatory.value,
      max_file_size_mb: maxFileSize,
      status: this.documentForm.controls.status.value || 'Active',
      effective_date: this.documentForm.controls.effective_date.value || null,
    };

    const editId = this.editingDocumentConfigId();
    const isDraftOnly = !editId || this.draftDocumentConfigs().some((item) => item.id === editId);
    if (isDraftOnly) {
      const row: DocumentConfig = {
        id: editId || this.tempId('doc'),
        module_name: payload.module_name || 'LOA Module',
        client_type: payload.client_type || 'LOA',
        client_types: payload.client_types || 'Buy, Sell, Banking',
        doc_title: payload.doc_title || '',
        file_type: payload.file_type || 'PDF',
        allowed_file_types: payload.allowed_file_types || payload.file_type || 'PDF',
        mandatory: !!payload.mandatory,
        max_file_size_mb: maxFileSize,
        status: payload.status || 'Active',
        effective_date: payload.effective_date ?? null,
        created_at: new Date().toISOString(),
      };
      if (editId) {
        this.draftDocumentConfigs.set(this.draftDocumentConfigs().map((item) => (item.id === editId ? row : item)));
      } else {
        this.draftDocumentConfigs.set([...this.draftDocumentConfigs(), row]);
      }
      this.toast.show(editId ? 'Document config updated' : 'Document config added', '', 'success');
      this.cancelDocumentConfig();
      return;
    }

    const req$ = editId
      ? this.loaService.updateDocumentConfig(editId, payload)
      : this.loaService.createDocumentConfig(payload);
    req$.subscribe({
      next: () => {
        this.toast.show(editId ? 'Document config updated' : 'Document config added', '', 'success');
        this.cancelDocumentConfig();
        this.loadDocumentConfigs();
      },
      error: () => this.toast.show('Failed to save document config', '', 'danger'),
    });
  }

  protected removeDocumentConfig(id: string): void {
    if (!confirm('Delete this document config?')) return;
    if (this.draftDocumentConfigs().some((item) => item.id === id)) {
      this.draftDocumentConfigs.set(this.draftDocumentConfigs().filter((item) => item.id !== id));
      this.toast.show('Document config deleted', '', 'success');
      return;
    }
    this.loaService.deleteDocumentConfig(id).subscribe({
      next: () => {
        this.toast.show('Document config deleted', '', 'success');
        this.loadDocumentConfigs();
      },
      error: () => this.toast.show('Failed to delete document config', '', 'danger'),
    });
  }

  protected openDocumentConfigForm(): void {
    this.editDocumentConfig(null);
  }

  protected closeDocumentUploadForm(): void {
    this.showDocumentUploadForm.set(false);
    this.selectedUploadFiles.set([]);
  }

  protected onUploadDocTypeChange(value: string): void {
    this.selectedUploadDocType.set(value || '');
    if ((value || '') !== 'Other') this.selectedUploadOtherDocType.set('');
  }

  protected onUploadOtherDocTypeChange(value: string): void {
    this.selectedUploadOtherDocType.set(value || '');
  }

  protected onLoaDocumentFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.selectedUploadFiles.set(Array.from(input.files || []));
  }

  protected uploadLoaDocument(): void {
    const vendorId = this.form.controls.vendor_id.value;
    if (!vendorId) {
      this.toast.show('Select Entity first', '', 'danger');
      return;
    }
    const files = this.selectedUploadFiles();
    if (!files.length) {
      this.toast.show('Select at least one file', '', 'danger');
      return;
    }
    const selectedType = this.selectedUploadDocType();
    const finalType =
      selectedType === 'Other' ? (this.selectedUploadOtherDocType() || '').trim() : (selectedType || '').trim();
    if (!finalType) {
      this.toast.show('Document type is required', '', 'danger');
      return;
    }
    if (this.hasUploadedLoaAgreement() && this.normalizeDocToken(finalType) === 'LOA AGREEMENT') {
      this.toast.show('LOA Agreement is already uploaded. Please select another document type from the dropdown.', '', 'danger');
      return;
    }
    const selectedConfig = this.activeLoaDocumentConfigs().find(
      (item) => this.normalizeDocToken(item.doc_title || item.document_name || '') === this.normalizeDocToken(finalType),
    );
    if (selectedType !== 'Other' && !selectedConfig) {
      this.toast.show('Selected document type is not available for this LOA category', '', 'danger');
      return;
    }
    if (selectedConfig) {
      const allowedExtensions = this.parseAllowedExtensions(selectedConfig.allowed_file_types || selectedConfig.file_type || '');
      const maxSizeMb = this.resolveMaxFileSizeMb(selectedConfig);
      const maxSizeBytes = maxSizeMb * 1024 * 1024;
      for (const file of files) {
        const ext = this.fileExtension(file.name);
        if (allowedExtensions.length && (!ext || !allowedExtensions.includes(ext))) {
          this.toast.show(
            `Invalid file type for ${finalType}. Allowed: ${allowedExtensions.map((item) => item.toUpperCase()).join(', ')}`,
            '',
            'danger',
          );
          return;
        }
        if (maxSizeMb > 0 && file.size > maxSizeBytes) {
          this.toast.show(`File "${file.name}" exceeds max size of ${maxSizeMb} MB`, '', 'danger');
          return;
        }
      }
    }

    this.uploadingLoaDocument.set(true);
    this.documentsService.uploadVendorDocuments(vendorId, finalType, files, this.loaId()).subscribe({
      next: () => {
        this.uploadingLoaDocument.set(false);
        this.toast.show('LOA documents uploaded', '', 'success');
        this.loadLoaDocuments(vendorId, this.loaId());
        this.closeDocumentUploadForm();
      },
      error: () => {
        this.uploadingLoaDocument.set(false);
        this.toast.show('Failed to upload LOA documents', '', 'danger');
      },
    });
  }

  protected openVendorPreview(vendorId?: string | null): void {
    const id = (vendorId || '').trim();
    if (!id) return;
    const url = this.router.serializeUrl(this.router.createUrlTree(['/vendors', id]));
    window.open(url, '_blank');
  }

  protected downloadLoaDocument(id: string): void {
    window.open(this.documentsService.downloadUrl(id), '_blank');
  }

  protected removeLoaDocument(id: string): void {
    if (!confirm('Delete this uploaded document?')) return;
    this.documentsService.deleteDocument(id).subscribe({
      next: () => {
        this.toast.show('Document deleted', '', 'success');
        const vendorId = this.form.controls.vendor_id.value;
        if (vendorId) this.loadLoaDocuments(vendorId, this.loaId());
      },
      error: () => this.toast.show('Failed to delete document', '', 'danger'),
    });
  }

  protected onMcpConditionChange(value: string): void {
    this.billConfigForm.patchValue({ mcp_condition: value || '' }, { emitEvent: false });
    this.syncSopMcpRateBehavior();
  }

  protected onBillRateTypeChange(value: string): void {
    this.billConfigForm.patchValue({ rate_type: value || '' }, { emitEvent: false });
    this.syncSopMcpRateBehavior();
  }

  protected openBillCycleView(): void {
    const cycle = this.billCycleSelection() || this.billConfigForm.controls.bill_cycle.value;
    if (!cycle) {
      this.toast.show('Select bill cycle first', '', 'info');
      return;
    }
    if (cycle === 'Custom' && this.customCycleRanges().length === 0) {
      this.customCycleRanges.set([{ id: this.tempId('range'), from: '1', to: 'Last Day' }]);
    }
    this.showBillCycleViewModal.set(true);
  }

  protected closeBillCycleView(): void {
    this.showBillCycleViewModal.set(false);
    this.editingCycleRangeId.set(null);
    this.rangeEditor.reset({ from: '', to: '' });
  }

  protected onBillCycleChange(value: string): void {
    this.billCycleSelection.set(value || '');
    this.billConfigForm.patchValue({ bill_cycle: value || '' }, { emitEvent: false });
  }

  private syncSopMcpRateBehavior(): void {
    const isSopMcp = this.billConfigForm.controls.bill_type.value === 'SOP' && this.billConfigForm.controls.rate_type.value === 'MCP';
    if (!isSopMcp) {
      this.billConfigForm.patchValue({ mcp_condition: '' }, { emitEvent: false });
      this.billConfigForm.controls.rate.enable({ emitEvent: false });
      return;
    }

    const condition = this.billConfigForm.controls.mcp_condition.value;
    const shouldLockRate = condition === 'Average' || condition === 'Equal';
    if (shouldLockRate) {
      this.billConfigForm.patchValue({ rate: '0' }, { emitEvent: false });
      this.billConfigForm.controls.rate.disable({ emitEvent: false });
    } else {
      this.billConfigForm.controls.rate.enable({ emitEvent: false });
    }
  }

  private applyBillTypeFieldRules(type: 'SOP' | 'TMR' | 'OA'): void {
    const rateControl = this.billConfigForm.controls.rate;
    if (type === 'OA') {
      rateControl.clearValidators();
      rateControl.disable({ emitEvent: false });
      rateControl.patchValue('', { emitEvent: false });
    } else {
      rateControl.setValidators([Validators.required]);
      rateControl.enable({ emitEvent: false });
    }
    rateControl.updateValueAndValidity({ emitEvent: false });
  }

  protected addOrUpdateCycleRange(): void {
    const from = this.rangeEditor.controls.from.value.trim();
    const to = this.rangeEditor.controls.to.value.trim();
    if (!from || !to) return;

    const parsedInput = this.parseCycleRangeInput(from, to);
    if (!parsedInput) return;

    const editId = this.editingCycleRangeId();
    const otherRows = this.customCycleRanges().filter((row) => row.id !== editId);
    const parsedOtherRows = otherRows.map((row) => ({ row, parsed: this.parseCycleRangeInput(row.from, row.to, false) }));

    if (parsedInput.start === 1 && parsedInput.end === 31 && parsedOtherRows.length > 0) {
      this.toast.show('1-Last Day can only be added as a single range', '', 'danger');
      return;
    }

    if (parsedOtherRows.some((item) => item.parsed?.start === 1 && item.parsed?.end === 31)) {
      this.toast.show('Range already covers 1-Last Day. No more entries allowed.', '', 'danger');
      return;
    }

    const duplicate = parsedOtherRows.some(
      (item) => item.parsed && item.parsed.start === parsedInput.start && item.parsed.end === parsedInput.end,
    );
    if (duplicate) {
      this.toast.show('Duplicate range is not allowed', '', 'danger');
      return;
    }

    const overlap = parsedOtherRows.some(
      (item) => item.parsed && parsedInput.start <= item.parsed.end && parsedInput.end >= item.parsed.start,
    );
    if (overlap) {
      this.toast.show('Overlapping or between range is not allowed', '', 'danger');
      return;
    }

    const nextRow: CycleRange = {
      id: editId || this.tempId('range'),
      from: parsedInput.displayFrom,
      to: parsedInput.displayTo,
    };
    const mergedRows = editId
      ? this.customCycleRanges().map((row) => (row.id === editId ? nextRow : row))
      : [...this.customCycleRanges(), nextRow];
    const sortedRows = [...mergedRows].sort((a, b) => {
      const left = this.parseCycleRangeInput(a.from, a.to, false)?.start ?? 0;
      const right = this.parseCycleRangeInput(b.from, b.to, false)?.start ?? 0;
      return left - right;
    });
    this.customCycleRanges.set(sortedRows);

    this.editingCycleRangeId.set(null);
    this.rangeEditor.reset({ from: '', to: '' });
  }

  private parseCycleRangeInput(
    from: string,
    to: string,
    showError: boolean = true,
  ): { start: number; end: number; displayFrom: string; displayTo: string } | null {
    const start = this.parseCycleDayValue(from, false);
    const end = this.parseCycleDayValue(to, true);
    if (start === null || end === null) {
      if (showError) this.toast.show('Use day values between 1 and 31, or "Last Day" for To value', '', 'danger');
      return null;
    }
    if (start > end) {
      if (showError) this.toast.show('From value cannot be greater than To value', '', 'danger');
      return null;
    }
    return {
      start,
      end,
      displayFrom: String(start),
      displayTo: this.isLastDayToken(to) ? 'Last Day' : String(end),
    };
  }

  private parseCycleDayValue(input: string, allowLastDay: boolean): number | null {
    const value = input.trim();
    if (!value) return null;
    if (allowLastDay && this.isLastDayToken(value)) return 31;
    if (!/^\d+$/.test(value)) return null;
    const day = Number(value);
    if (!Number.isInteger(day) || day < 1 || day > 31) return null;
    return day;
  }

  private isLastDayToken(value: string): boolean {
    const normalized = value.trim().toLowerCase().replace(/\s+/g, '');
    return normalized === 'lastday' || normalized === 'last';
  }

  protected formatClientTypes(value: string | undefined | null): string {
    const values = this.parseClientTypes(value);
    return values.length ? values.join(', ') : 'All';
  }

  private isDocumentConfigForSelectedCategory(item: DocumentConfig): boolean {
    if ((item.status || 'Active').toUpperCase() !== 'ACTIVE') return false;
    const configuredCategories = this.parseClientTypes(item.client_types);
    if (!configuredCategories.length) return true;
    const loaCategoryTokens = ['BUY', 'SELL', 'BANKING'];
    const hasLoaCategoryToken = configuredCategories.some((token) => loaCategoryTokens.includes(token));
    if (!hasLoaCategoryToken) return true;
    const selected = this.selectedCategoryUpper();
    return configuredCategories.includes(selected);
  }

  private isDocumentConfigForSelectedEntityType(item: DocumentConfig): boolean {
    const vendorId = (this.form.controls.vendor_id.value || '').trim();
    if (!vendorId) return true;
    const selectedVendor = this.vendors().find((vendor) => vendor.id === vendorId);
    const selectedEntityType = this.normalizeClientTypeToken(selectedVendor?.vendor_type || '');
    if (!selectedEntityType) return true;
    const configuredTypes = this.parseClientTypes(item.client_types);
    if (!configuredTypes.length) return true;
    return configuredTypes.some((type) => this.normalizeClientTypeToken(type) === selectedEntityType);
  }

  private parseClientTypes(value: string | undefined | null): string[] {
    const raw = (value || '').trim();
    if (!raw) return [];
    const normalizedRaw = raw.startsWith('[') ? raw.replace(/^\[|\]$/g, '') : raw;
    return normalizedRaw
      .split(',')
      .map((item) => this.normalizeDocToken(item.replace(/['"]/g, '')))
      .filter((item) => !!item);
  }

  private normalizeDocToken(value: string | undefined | null): string {
    return (value || '')
      .trim()
      .replace(/\s+/g, ' ')
      .toUpperCase();
  }

  private normalizeClientTypeToken(value: string | undefined | null): string {
    const token = this.normalizeDocToken(value);
    if (!token) return '';
    if (token === 'TRADER') return 'VENDOR';
    if (token === 'STATE') return 'STATE';
    if (token === 'DISCOM') return 'DISCOM';
    if (token === 'GENERATOR') return 'GENERATOR';
    if (token === 'IPP') return 'IPP';
    if (token === 'INDUSTRY' || token === 'C&I' || token === 'C & I' || token === 'C AND I') return 'C&I';
    return token;
  }

  private resolveDocumentMandatory(item: DocumentConfig): boolean {
    if (typeof item.is_mandatory === 'boolean') return item.is_mandatory;
    return !!item.mandatory;
  }

  private resolveMaxFileSizeMb(item: DocumentConfig): number {
    const numeric = Number(item.max_file_size_mb ?? item.max_file_size ?? 10);
    if (!Number.isFinite(numeric) || numeric <= 0) return 10;
    return numeric;
  }

  private parseAllowedExtensions(value: string): string[] {
    const tokens = (value || '')
      .split(',')
      .map((item) => item.trim().toLowerCase().replace(/^\./, ''))
      .filter((item) => !!item);
    return Array.from(new Set(tokens));
  }

  private fileExtension(fileName: string): string {
    const parts = (fileName || '').toLowerCase().split('.');
    if (parts.length < 2) return '';
    return parts[parts.length - 1];
  }

  private syncBillingRunsFromConfigs(items: LoaBillConfig[]): void {
    const normalizedItems = [...(items || [])];
    const types = new Set(normalizedItems.map((item) => (item.bill_type || '').trim().toUpperCase()));
    const settlementConfigs = normalizedItems
      .filter((item) => ['SOP', 'TMR'].includes((item.bill_type || '').trim().toUpperCase()))
      .sort((a, b) => {
        const left = (b.effective_date || '').localeCompare(a.effective_date || '');
        if (left !== 0) return left;
        return (b.id || '').localeCompare(a.id || '');
      });
    const primarySettlementType = settlementConfigs[0]?.bill_type?.trim().toUpperCase() || '';
    this.billingRuns.set({
      SOP: primarySettlementType ? primarySettlementType === 'SOP' : !types.has('TMR'),
      TMR: primarySettlementType === 'TMR',
      OA: types.has('OA'),
    });
  }

  private validateLoaBusinessRules(stage: 'submit' | 'approve'): string | null {
    const issueDate = this.form.controls.issue_date.value || '';
    const effectiveFrom = this.form.controls.effective_from.value || '';
    const effectiveTo = this.form.controls.effective_to.value || '';
    const energyType = (this.form.controls.energy_type.value || '').trim();

    if (effectiveFrom && issueDate && effectiveFrom < issueDate) {
      return 'Effective From date cannot be earlier than Issue Date';
    }
    if (effectiveTo && effectiveFrom && effectiveTo < effectiveFrom) {
      return 'Effective To date cannot be earlier than Effective From date';
    }
    if (this.isEnergyTypeRequired() && !energyType) {
      return 'Energy Type is required for generator entities';
    }
    if (this.isBanking() && !this.form.controls.banking_priority.value) {
      return 'Banking Priority is required for Banking LOA';
    }
    const penaltyError = this.validatePenaltyConfiguration();
    if (penaltyError) {
      return penaltyError;
    }

    const schedules = this.displaySchedules();
    if (!schedules.length) {
      return 'At least one schedule is required';
    }
    if (this.isBanking()) {
      const hasImport = schedules.some((item) => (item.direction || 'import') === 'import');
      const hasExport = schedules.some((item) => (item.direction || 'import') === 'export');
      if (!hasImport || !hasExport) {
        return 'Banking LOA requires both Import and Export schedules';
      }
    }

    const missingMandatory = this.missingMandatoryLoaDocumentNames();
    if (missingMandatory.length) {
      return `Upload mandatory documents first: ${missingMandatory.join(', ')}`;
    }

    const selectedRuns = this.billingRuns();
    if (!selectedRuns.SOP && !selectedRuns.TMR && !selectedRuns.OA) {
      return 'At least one application must be selected';
    }
    if (selectedRuns.SOP && !this.hasBillingConfig('SOP')) {
      return 'SOP billing configuration is required';
    }
    if (selectedRuns.TMR && !this.hasBillingConfig('TMR')) {
      return 'TMR billing configuration is required';
    }
    if (selectedRuns.OA && !this.hasBillingConfig('OA')) {
      return 'OA billing configuration is required';
    }

    if (stage === 'approve' && !['pending_initial_approval', 'pending_accounts_approval', 'pending_operation_approval'].includes(this.loaStatus())) {
      return 'Only pending approval LOA can be approved';
    }
    return null;
  }

  protected editCycleRange(row: CycleRange): void {
    this.editingCycleRangeId.set(row.id);
    this.rangeEditor.patchValue({ from: row.from, to: row.to });
  }

  protected removeCycleRange(id: string): void {
    this.customCycleRanges.set(this.customCycleRanges().filter((row) => row.id !== id));
  }

  protected updateOaAllocation(index: number, key: 'payer' | 'buyer_percent' | 'seller_percent', value: string): void {
    const rows = [...this.oaChargeAllocations()];
    if (!rows[index]) return;
    if (key === 'payer') {
      rows[index].payer = (value as 'Buyer' | 'Seller' | 'Shared') || 'Shared';
    } else {
      const parsed = Number(value);
      rows[index][key] = Number.isFinite(parsed) ? parsed : 0;
    }
    this.oaChargeAllocations.set(rows);
  }

  private persistDraftChildren(loaId: string) {
    const scheduleRequests = this.draftSchedules().map((item) =>
      this.loaService.createSchedule(loaId, {
        from_date: item.from_date,
        to_date: item.to_date,
        from_time: item.from_time,
        to_time: item.to_time,
        arrangement: item.arrangement ?? null,
        quantum_mw: item.quantum_mw ?? null,
        return_percentage: item.return_percentage ?? null,
        direction: item.direction,
      }),
    );
    const emdRequests = this.draftEmds().map((item) =>
      this.loaService.createEmd(loaId, {
        security_deposit_type: item.security_deposit_type,
        issue_date: item.issue_date,
        effective_from: item.effective_from,
        effective_to: item.effective_to,
        bank_name: item.bank_name,
        reference_number: item.reference_number,
        amount: item.amount,
      }),
    );
    const billRequests = this.draftBillConfigs().map((item) =>
      this.loaService.createBillConfig(loaId, {
        bill_type: item.bill_type,
        bill_cycle: item.bill_cycle ?? null,
        effective_date: item.effective_date ?? null,
        rate_type: item.rate_type ?? null,
        mcp_condition: item.mcp_condition ?? null,
        mcp_rate: item.mcp_rate ?? null,
        tmr_rate: item.tmr_rate ?? null,
        rate: item.rate ?? null,
        due_days: item.due_days ?? null,
        rebate: item.rebate ?? null,
        rebate_days: item.rebate_days ?? null,
        discount_price: item.discount_price ?? null,
        oa_charge_allocation: item.oa_charge_allocation ?? null,
      }),
    );
    const requests = [...scheduleRequests, ...emdRequests, ...billRequests];
    return requests.length ? forkJoin(requests) : of([]);
  }

  private tempId(prefix: string): string {
    this.tempIdCounter += 1;
    return `${prefix}-${Date.now()}-${this.tempIdCounter}`;
  }

  private normalizeLoaCategory(value: string | null | undefined): 'Buy' | 'Sell' | 'Banking' {
    const normalized = (value || '').trim().toLowerCase();
    if (normalized === 'sell') return 'Sell';
    if (normalized === 'banking') return 'Banking';
    return 'Buy';
  }

  private asNullable(value: string | null | undefined): string | null {
    return value && value.trim().length > 0 ? value : null;
  }

  private asNumberOrNull(value: string | number | null | undefined): number | null {
    if (value === null || value === undefined || value === '') return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private asIntOrNull(value: string | number | null | undefined): number | null {
    if (value === null || value === undefined || value === '') return null;
    const parsed = parseInt(String(value), 10);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private scheduleArrangementValue(item: LoaSchedule): number {
    const parsed = this.asIntOrNull(item.arrangement as string | number | null | undefined);
    return parsed && parsed > 0 ? parsed : this.fallbackScheduleArrangement(item);
  }

  private fallbackScheduleArrangement(item: LoaSchedule): number {
    const direction = this.normalizeScheduleDirection(item.direction, 'import');
    const items = [...this.displaySchedules()]
      .filter((row) => this.normalizeScheduleDirection(row.direction, 'import') === direction)
      .sort((left, right) =>
        (left.from_date || '').localeCompare(right.from_date || '') ||
        (left.to_date || '').localeCompare(right.to_date || '') ||
        this.normalizeScheduleTime(left.from_time, false).localeCompare(this.normalizeScheduleTime(right.from_time, false)) ||
        this.normalizeScheduleTime(left.to_time, true).localeCompare(this.normalizeScheduleTime(right.to_time, true)) ||
        (left.id || '').localeCompare(right.id || ''),
      );
    const index = items.findIndex((row) => row.id === item.id);
    return index >= 0 ? index + 1 : 1;
  }

  private normalizeScheduleDirection(value: string | null | undefined, fallback: 'import' | 'export' = 'import'): 'import' | 'export' {
    const normalized = (value || '').trim().toLowerCase();
    if (normalized === 'export') return 'export';
    if (normalized === 'import') return 'import';
    return fallback;
  }

  private normalizeScheduleTime(value: string | null | undefined, allowEndOfDay: boolean): string {
    const raw = (value || '').trim();
    if (!raw) return '';
    const match = raw.match(/^(\d{1,2}):(\d{1,2})(?::\d{1,2})?$/);
    if (!match) return raw;
    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    if (!Number.isInteger(hours) || !Number.isInteger(minutes)) return raw;
    const normalized = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    return this.isQuarterHourTime(normalized, allowEndOfDay) ? normalized : raw;
  }

  private nextArrangementForDirection(direction: 'import' | 'export'): number {
    const used = this.displaySchedules()
      .filter((item) => (item.direction || 'import') === direction)
      .map((item) => this.scheduleArrangementValue(item))
      .filter((value) => Number.isFinite(value) && value > 0 && value < Number.MAX_SAFE_INTEGER);
    for (let candidate = 1; candidate <= used.length + 1; candidate += 1) {
      if (!used.includes(candidate)) return candidate;
    }
    return used.length + 1;
  }

  private isQuarterHourTime(value: string, allowEndOfDay: boolean): boolean {
    if (!value || !/^\d{2}:\d{2}$/.test(value)) return false;
    const [hh, mm] = value.split(':').map((part) => Number(part));
    if (!Number.isInteger(hh) || !Number.isInteger(mm)) return false;
    if (mm < 0 || mm > 59 || mm % 15 !== 0) return false;
    if (allowEndOfDay && hh === 24 && mm === 0) return true;
    return hh >= 0 && hh <= 23;
  }

  private resolveBillCycleValue(): string | null {
    const cycle = this.billConfigForm.controls.bill_cycle.value;
    if (!cycle) return null;
    if (cycle !== 'Custom') return cycle;
    const ranges = this.customCycleRanges();
    if (!ranges.length) return 'Custom';
    const serialized = ranges.map((row) => `${row.from}-${row.to}`).join(',');
    return `Custom:${serialized}`;
  }

  private resetOaAllocationDefaults(): void {
    this.oaChargeAllocations.set(
      OA_CHARGE_ROWS.map((charge) => ({
        charge_type: charge,
        payer: 'Shared',
        buyer_percent: 50,
        seller_percent: 50,
      })),
    );
  }

  private parseOaChargeAllocation(raw: string | null | undefined): OaChargeAllocation[] {
    const defaults = OA_CHARGE_ROWS.map((charge) => ({
      charge_type: charge,
      payer: 'Shared' as const,
      buyer_percent: 50,
      seller_percent: 50,
    }));
    if (!raw || !raw.trim()) return defaults;
    try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return defaults;
      const byCharge = new Map<string, OaChargeAllocation>();
      for (const row of parsed) {
        if (!row || typeof row !== 'object') continue;
        const charge_type = String((row as Partial<OaChargeAllocation>).charge_type || '').trim();
        if (!charge_type) continue;
        const payerInput = String((row as Partial<OaChargeAllocation>).payer || 'Shared');
        const payer: OaChargeAllocation['payer'] =
          payerInput === 'Buyer' || payerInput === 'Seller' || payerInput === 'Shared' ? payerInput : 'Shared';
        const buyer = Number((row as Partial<OaChargeAllocation>).buyer_percent);
        const seller = Number((row as Partial<OaChargeAllocation>).seller_percent);
        byCharge.set(charge_type, {
          charge_type,
          payer,
          buyer_percent: Number.isFinite(buyer) ? buyer : 0,
          seller_percent: Number.isFinite(seller) ? seller : 0,
        });
      }
      return defaults.map((row) => byCharge.get(row.charge_type) || row);
    } catch {
      return defaults;
    }
  }

  private parsePenaltySlabs(raw: string | null | undefined): PenaltyQuantumSlab[] {
    if (!raw || !raw.trim()) return [];
    try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed
        .map((row, index) => {
          if (!row || typeof row !== 'object') return null;
          const fromUnit = Number((row as Partial<PenaltyQuantumSlab>).from_unit);
          const toUnit = Number((row as Partial<PenaltyQuantumSlab>).to_unit);
          const rate = Number((row as Partial<PenaltyQuantumSlab>).rate);
          if (!Number.isFinite(fromUnit) || !Number.isFinite(toUnit) || !Number.isFinite(rate)) return null;
          return {
            id: String((row as Partial<PenaltyQuantumSlab>).id || `penalty-${index}`),
            from_unit: fromUnit,
            to_unit: toUnit,
            rate,
          };
        })
        .filter((row): row is PenaltyQuantumSlab => !!row);
    } catch {
      return [];
    }
  }

  private serializePenaltySlabs(rows: PenaltyQuantumSlab[]): string | null {
    if (!rows.length) return null;
    return JSON.stringify(
      [...rows]
        .sort((a, b) => a.from_unit - b.from_unit || a.to_unit - b.to_unit)
        .map(({ id, from_unit, to_unit, rate }) => ({ id, from_unit, to_unit, rate })),
    );
  }

  private validatePenaltyConfiguration(): string | null {
    if (!this.form.controls.penalty_applicable.value) return null;
    const mode = this.form.controls.penalty_mode.value || 'fixed_rate';
    if (mode === 'fixed_rate') {
      const fixedRate = this.asNumberOrNull(this.form.controls.penalty_fixed_rate.value);
      if (fixedRate === null || fixedRate < 0) {
        return 'Penalty fixed rate is required';
      }
      return null;
    }
    if (mode === 'quantum_wise') {
      const slabs = this.sortedPenaltySlabs();
      if (!slabs.length) {
        return 'Add at least one penalty slab for quantum-wise penalty';
      }
      for (const row of slabs) {
        if (row.from_unit < 0 || row.to_unit < row.from_unit || row.rate < 0) {
          return 'Penalty slab values are invalid';
        }
      }
      for (let index = 1; index < slabs.length; index += 1) {
        if (slabs[index].from_unit <= slabs[index - 1].to_unit) {
          return 'Penalty slab ranges must not overlap';
        }
      }
      return null;
    }
    return 'Penalty mode is invalid';
  }

  private validateBillingConfig(): boolean {
    const value = this.billConfigForm.getRawValue();
    const type = value.bill_type as 'SOP' | 'TMR' | 'OA';
    const requiredCommon =
      type === 'OA'
        ? [value.effective_date, value.bill_cycle, value.due_days, value.rebate, value.rebate_days]
        : [value.effective_date, value.bill_cycle, value.rate, value.due_days, value.rebate, value.rebate_days];
    if (requiredCommon.some((item) => !item || String(item).trim() === '')) {
      this.toast.show('Please fill all required billing fields', '', 'danger');
      return false;
    }
    if (type === 'SOP') {
      if (!value.rate_type || String(value.rate_type).trim() === '') {
        this.toast.show('Rate Type is required for SOP billing', '', 'danger');
        return false;
      }
      if (value.rate_type === 'MCP' && (!value.mcp_condition || String(value.mcp_condition).trim() === '')) {
        this.toast.show('MCP Condition is required for SOP billing', '', 'danger');
        return false;
      }
      if (value.bill_cycle === 'Custom' && (!value.custom_bill_cycle_view || String(value.custom_bill_cycle_view).trim() === '')) {
        if (this.customCycleRanges().length === 0) {
          this.toast.show('Custom bill cycle ranges are required for SOP billing', '', 'danger');
          return false;
        }
      }
    }
    if (type === 'TMR') {
      if (value.bill_cycle === 'Custom' && this.customCycleRanges().length === 0) {
        this.toast.show('Custom bill cycle ranges are required for TMR billing', '', 'danger');
        return false;
      }
    }
    if (type === 'OA') {
      if (!value.discount_price || String(value.discount_price).trim() === '') {
        this.toast.show('Discount Price is required for OA billing', '', 'danger');
        return false;
      }
      if (value.bill_cycle === 'Custom' && this.customCycleRanges().length === 0) {
        this.toast.show('Custom bill cycle ranges are required for OA billing', '', 'danger');
        return false;
      }
      if (this.invalidOaRows().length > 0) {
        this.toast.show('For OA allocation, Buyer % + Seller % must be 100 for each row', '', 'danger');
        return false;
      }
    }
    return true;
  }

  protected setProvisional(value: string): void {
    this.form.patchValue({ is_provisional: value === 'Yes' });
  }
}
